/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React from "react";
import moment from "moment";
import startCase from "lodash/startCase";
import { Typography, Box, Link, Modal } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from "@mui/icons-material/Visibility";

import ExtraOralContent from "./ExtraOralContent";
import { tl, t } from "../../../components/translate";
import EyeAssessmentContent from "./EyeAssessmentContent";
import useMobileScreen from "../../../hooks/useMobileScreen";
import { formatPrescriptionSummaryText } from "../../../helpers/prescriptionSummaryFormat";
import { AssessmentLabel } from "../../../hooks/useAssessmentLabel";
import { Assessment } from "../../../interfaces/AssessmentInterfaces";
import DiagnosisRenderer, { hasDiagnosis } from "./DiagnosisRenderer";
import IntraoralAssessmentContent from "./IntraoralAssessmentContent";
import { FilePreview } from "../../../components/FilesInput/FilePreview";
import { countableTypes } from "../../Assessment/Medication/medicineType";
import { SORT_ORDER } from "../../Assessment/AssessmentForm/OPDComponents";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import AdvancedIntraOralContent, { hasContentAdvancedIntraOral } from "./AdvancedIntraOralContent";

import {
  getMedicineMultiplier,
  showFrequencyUnit,
  showMeal,
  showTimeUnit
} from "../../Assessment/Medication/Medication";

import {
  EditableTableView,
  filterEmptyTableData
} from "../../../components/EditableTable/EditableTable";
import { getFollowupFormat } from "./AssessmentContent";

// @ts-expect-error - css
import styles from "./styles.module.scss";

// helpers
interface dataToPrint {
  name1?: string;
  value1?: string;
  name2?: string;
  value2?: string;
  name3?: string;
  value3?: string;
}

export const formatAssessmentForPrint = (
  data: { name?: string; reading?: string; unit?: string }[]
): dataToPrint[] => {
  const dataToPrint: dataToPrint[] = [];
  for (let i = 0; i < data?.length; i += 3) {
    const dataRow = {
      name1: data[i].name,
      value1: `${data[i].reading} ${data[i].unit}`,
      name2: data[i + 1]?.name,
      value2: `${data[i + 1]?.reading} ${data[i + 1]?.unit}`,
      name3: data[i + 2]?.name,
      value3: `${data[i + 2]?.reading} ${data[i + 2]?.unit}`
    };
    dataToPrint.push(dataRow);
  }
  return dataToPrint;
};

interface AssessmentComponentProp {
  componentName: string;
  data: Assessment;
  assessmentLabels: AssessmentLabel;
  showAttachment: boolean;
}

// eslint-disable-next-line import/prefer-default-export
export const AssessmentComponent = ({
  componentName,
  data,
  assessmentLabels,
  showAttachment
}: AssessmentComponentProp): React.ReactNode => {
  const vitalToPrint = formatAssessmentForPrint(data.vitals);
  const questionnaireToPrint = formatAssessmentForPrint(data.questionnaire);

  const servicesAndTests = [...(data?.tests || []), ...(data?.services || [])];
  const tabularComponentData = data.tabularComponent && filterEmptyTableData(data.tabularComponent);
  const [currentAttachment, setCurrentAttachment] = React.useState(null);
  const [openPreview, setOpenPreview] = React.useState(false);
  const productMedication = data.productMedication ? data.productMedication : [];
  const isMobileScreen = useMobileScreen();

  switch (componentName) {
    case SORT_ORDER.RECORD_PRESCRIPTION: {
      const prescriptionText = data.prescriptionAudioSummary
        ? formatPrescriptionSummaryText(data.prescriptionAudioSummary)
        : data.prescriptionAudioTranscription;

      return data.prescriptionAudioUrl || prescriptionText ? (
        <Box pb={2}>
          <Typography sx={{ fontWeight: 600 }}>{assessmentLabels.recordPrescription}</Typography>
          {data.prescriptionAudioUrl && (
            /* eslint-disable-next-line jsx-a11y/media-has-caption */
            <audio controls src={data.prescriptionAudioUrl} />
          )}
          {prescriptionText && (
            <Typography sx={{ mt: 1, whiteSpace: "pre-wrap" }}>{prescriptionText}</Typography>
          )}
        </Box>
      ) : null;
    }

    case SORT_ORDER.CHIEF_COMPLAINTS:
      return (
        <Box>
          {data.symptoms?.length > 0 ? (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.chiefComplaints}
                  </Box>
                </Typography>
                <Typography>
                  <Box display="flex" component="span">
                    {data.symptoms.map((symptom) => symptom.symptom).join(", ")}
                  </Box>
                </Typography>
              </Box>
            </>
          ) : null}
        </Box>
      );

    case SORT_ORDER.PAST_HISTORY_OF_ALLERGY:
      return (
        <Box>
          {data.pastHistoryOfAllergy ? (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.pastHistoryOfAllergy}
                  </Box>
                </Typography>
                <Typography>
                  <Box component="span">{data.pastHistoryOfAllergy}</Box>
                </Typography>
              </Box>
            </>
          ) : null}
        </Box>
      );

    case SORT_ORDER.HOPI:
      return data.hopi ? (
        <>
          <Box pb={2}>
            <Typography>
              <Box pt={3} fontWeight={600} component="span">
                {assessmentLabels.hopi}
              </Box>
            </Typography>
            <Typography>
              <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                {data.hopi}
              </Box>
            </Typography>
          </Box>
        </>
      ) : null;

    case SORT_ORDER.EXAMINATION_OR_STATUS:
      return (
        <>
          {data.assessment ? (
            <Box>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.examination}
                  </Box>
                </Typography>

                <Typography>
                  <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                    {data.assessment}
                  </Box>
                </Typography>
              </Box>
            </Box>
          ) : null}
          <Box>
            {data.assessmentImage?.length > 0 && (
              <>
                <Box pb={2}>
                  <img
                    src={data.assessmentImage}
                    className={styles.assessmentImg}
                    alt="Assessment"
                  />
                </Box>
              </>
            )}
          </Box>
        </>
      );

    case SORT_ORDER.INVESTIGATION:
      return (
        <div>
          {data.investigation ? (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.investigation}
                  </Box>
                </Typography>

                <Typography>
                  <Box component="span" whiteSpace="pre-line">
                    {data.investigation}
                  </Box>
                </Typography>
              </Box>
            </>
          ) : null}
        </div>
      );

    case SORT_ORDER.INTRA_ORAL:
      return (
        <Box>
          {data?.intraOral &&
            (Object.keys(data.intraOral.primary).length !== 0 ||
              Object.keys(data.intraOral.permanent).length !== 0) && (
              <>
                <Box pb={2}>
                  <Typography>
                    <Box pt={3} fontWeight={600} component="span">
                      {assessmentLabels.intraoral}
                    </Box>
                  </Typography>
                  <IntraoralAssessmentContent intraOralDoc={data.intraOral} />
                </Box>
              </>
            )}
        </Box>
      );

    case SORT_ORDER.EXTRA_ORAL:
      return Object.keys(data.extraOral || {}).some((key) => !!data.extraOral[key]) ? (
        <ExtraOralContent
          assessmentDocument={data}
          mode="view"
          componentLabel={assessmentLabels.extraOral}
        />
      ) : null;

    case SORT_ORDER.ADVANCED_INTRA_ORAL:
      return data.advancedIntraOral && hasContentAdvancedIntraOral(data.advancedIntraOral) ? (
        <AdvancedIntraOralContent
          assessmentDocument={data}
          mode="view"
          componentLabel={assessmentLabels.advancedIntraOral}
        />
      ) : null;

    case SORT_ORDER.EYE:
      return (
        <Box>
          {data?.eye && Object.keys(data?.eye).length > 0 && (
            <EyeAssessmentContent eyeExaminationDoc={data?.eye} />
          )}
        </Box>
      );

    case SORT_ORDER.QUESTIONNAIRE:
      return questionnaireToPrint?.length > 0 ? (
        <>
          <Typography fontWeight="bold" marginTop="10px">
            {assessmentLabels.questionnaire}:
          </Typography>
          <Box width="100%">
            {questionnaireToPrint.map((questionnaire, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={index} display="flex" justifyContent="space-between" flexWrap="wrap">
                <Box display="flex" flexBasis="230px">
                  <Typography fontWeight="bold">{startCase(questionnaire.name1)}</Typography>
                  <Typography> : {questionnaire.value1}</Typography>
                </Box>
                <Box display="flex" flexBasis="230px">
                  <Typography fontWeight="bold">{startCase(questionnaire.name2)}</Typography>
                  {questionnaire.name2 && <Typography> : {questionnaire.value2}</Typography>}
                </Box>
                <Box display="flex" flexBasis="230px">
                  <Typography fontWeight="bold">{startCase(questionnaire.name3)}</Typography>
                  {questionnaire.name3 && <Typography> : {questionnaire.value3}</Typography>}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      ) : null;

    case SORT_ORDER.VITALS:
      return vitalToPrint?.length > 0 ? (
        <>
          <Typography fontWeight="bold" marginTop="10px">
            {assessmentLabels.vitals}:
          </Typography>
          {vitalToPrint.map((vital, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index} display="flex" justifyContent="space-between">
              <Box display="flex" width="33%">
                <Typography fontWeight="bold">{startCase(vital.name1)}</Typography>
                <Typography> : {vital.value1}</Typography>
              </Box>
              <Box display="flex" width="33%">
                <Typography fontWeight="bold">{startCase(vital.name2)}</Typography>
                {vital.name2 && <Typography> : {vital.value2}</Typography>}
              </Box>
              <Box display="flex" width="33%">
                <Typography fontWeight="bold">{startCase(vital.name3)}</Typography>
                {vital.name3 && <Typography> : {vital.value3}</Typography>}
              </Box>
            </Box>
          ))}
        </>
      ) : null;

    case SORT_ORDER.PROVISIONAL_DIAGNOSIS:
      return (
        <Box>
          {data.provisionalDiagnosis && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.provisionalDiagnosis}
                  </Box>
                </Typography>
                <Typography>
                  <Box component="span">{data.provisionalDiagnosis}</Box>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.LAB_TEST:
      return (
        <Box>
          {servicesAndTests?.length > 0 && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.labTest}
                  </Box>
                </Typography>
                <ol style={{ paddingLeft: "1.5rem", margin: 0 }}>
                  {servicesAndTests.map(({ code, name, id }) => (
                    <li key={code || id}>
                      <Typography>{name}</Typography>
                    </li>
                  ))}
                </ol>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.DIAGNOSIS:
      return hasDiagnosis(data.diagnosis) ? (
        <Box pb={2}>
          <Typography pt={3} fontWeight={600} component="span">
            {assessmentLabels.diagnosis}
          </Typography>
          <DiagnosisRenderer data={data.diagnosis} />
        </Box>
      ) : null;

    case SORT_ORDER.TABULAR:
      return tabularComponentData ? <EditableTableView tableState={tabularComponentData} /> : null;

    case SORT_ORDER.PRESENT_HEALTH_STATUS:
      return (
        <Box>
          {data.presentHealthStatus && (
            <Box display="flex" mb={2}>
              <Typography>
                <Box pt={3} fontWeight={600} component="span">
                  {assessmentLabels.presentHealthStatus}
                </Box>
              </Typography>
              <Typography>
                <Box component="span" pl={1}>
                  {data.presentHealthStatus}
                </Box>
              </Typography>
            </Box>
          )}
        </Box>
      );

    case SORT_ORDER.ATTACHMENTS:
      return (
        <Box>
          {showAttachment && data.attachments?.length > 0 && (
            <Box pb={2}>
              <Typography>
                <Box pt={3} pb="5px" fontWeight={600} component="span">
                  {assessmentLabels.attachments}
                </Box>
              </Typography>

              <Modal open={openPreview}>
                <FilePreview
                  onClose={() => {
                    setOpenPreview(false);
                  }}
                  fileDetails={currentAttachment}
                />
              </Modal>

              <Box>
                <ol style={{ paddingLeft: "1.5rem", margin: 0 }}>
                  {Array.isArray(data.attachments) &&
                    data.attachments.map((attachment) => (
                      <Box
                        display="flex"
                        style={{ textAlign: "center", alignItems: "center" }}
                        key={attachment.fileName}
                      >
                        <img
                          style={{
                            width: "80px",
                            height: "100px",
                            objectFit: "cover",
                            margin: "40px",
                            boxShadow: "1px 1px 2px 1px gray"
                          }}
                          src={attachment.downloadLink}
                          alt={attachment.fileName}
                          onClick={() => {
                            setOpenPreview(true);
                            setCurrentAttachment({
                              name: attachment.fileName,
                              url: attachment.downloadLink
                            });
                          }}
                          onKeyDown={null}
                        />
                        <Link
                          key={attachment.fileName}
                          href={attachment.downloadLink}
                          target="_blank"
                        >
                          <li>
                            <Box display="flex">
                              <Box>
                                <Typography>{attachment.fileName}</Typography>
                              </Box>
                              <Box>
                                <AttachFileIcon />
                              </Box>
                            </Box>
                          </li>
                        </Link>
                        <VisibilityIcon
                          onClick={() => {
                            setOpenPreview(true);
                            setCurrentAttachment({
                              name: attachment.fileName,
                              url: attachment.downloadLink
                            });
                          }}
                        />
                      </Box>
                    ))}
                </ol>
              </Box>
            </Box>
          )}
        </Box>
      );

    case SORT_ORDER.TREATMENT_PLAN:
      return (
        <Box>
          {data?.treatmentPlan && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.treatmentPlan}
                  </Box>
                </Typography>
                <Typography>
                  <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                    {data.treatmentPlan}
                  </Box>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.TREATMENT_GIVEN:
      return (
        <Box>
          {data?.treatmentGiven && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.treatmentGiven}
                  </Box>
                </Typography>
                <Typography>
                  <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                    {data.treatmentGiven}
                  </Box>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.MEDICATION:
      return (
        <Box>
          {(data.medication?.length > 0 || data.productMedication?.length > 0) && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {tl("assessment.medicationAndSupplements")}
                  </Box>
                </Typography>

                <Box>
                  <ol style={{ paddingLeft: "15px", margin: 0 }}>
                    {[...data.medication, ...productMedication]?.map(
                      ({
                        id,
                        duration,
                        form,
                        meal,
                        brand,
                        genericName,
                        remark,
                        frequency,
                        frequencyType,
                        strength,
                        times,
                        partsOfDay,
                        timesOfDay
                      }) => (
                        <li key={id} style={{ paddingTop: "5px" }}>
                          <Typography>
                            <Box component="span">
                              {" "}
                              {`${genericName || ""}${
                                genericName && brand && "/"
                              } ${brand}${" "}${strength}${" "} ${form ? `(${form})` : ""} `}{" "}
                            </Box>
                          </Typography>

                          <Box display={`${isMobileScreen ? "block" : "flex"}`} fontWeight={450}>
                            <Typography>
                              <Box component="span">
                                {frequency === 0 ? "" : `${frequency} ${t("assessment.times")}`}
                                {frequency !== 0 && showTimeUnit(times)}
                                {frequency !== 0 && <br />}
                                {showMeal(meal)}
                              </Box>
                            </Typography>

                            <Typography>
                              {frequency !== 0 && (
                                <Box pl={`${isMobileScreen ? "0px" : "32px"}`} component="span">
                                  {`${
                                    countableTypes.includes(form)
                                      ? frequency *
                                        getMedicineMultiplier(times, duration, frequencyType)
                                      : ""
                                  } ${form} (${duration} ${showFrequencyUnit(
                                    frequencyType,
                                    duration
                                  )})`}
                                </Box>
                              )}
                            </Typography>
                          </Box>

                          {partsOfDay && partsOfDay.length > 0 && (
                            <Typography>
                              <Box fontWeight={450} component="span" fontSize="0.8rem">
                                {`${t("assessment.medication.partsOfDay")} : ${partsOfDay
                                  .map((tod) => t(tod) || tod)
                                  .join(", ")}`}
                              </Box>
                            </Typography>
                          )}

                          {timesOfDay && timesOfDay.length > 0 && (
                            <Typography>
                              <Box fontWeight={450} component="span" fontSize="0.8rem">
                                {`${t("assessment.medication.timesOfDay")} : ${timesOfDay.join(
                                  ", "
                                )}`}
                              </Box>
                            </Typography>
                          )}

                          {remark && (
                            <Typography>
                              <Box fontWeight={450} component="span" fontSize="0.8rem">
                                <Box component="span">{`${t(
                                  "assessment.medication.remark"
                                )} : ${remark}`}</Box>
                              </Box>
                            </Typography>
                          )}
                        </li>
                      )
                    )}
                  </ol>
                </Box>
              </Box>
            </>
          )}
        </Box>
      );

    // case SORT_ORDER.PRODUCT_MEDICATION:
    case SORT_ORDER.FOLLOWUP:
      return (
        <Box>
          {data.followup && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.followup}{" "}
                  </Box>
                </Typography>

                <Box>
                  {data.followup?.days && (
                    <Typography component="span">{`Follow up in ${
                      data.followup.days
                    } ${getFollowupFormat(data.followup)} ${data.followup.note}`}</Typography>
                  )}

                  {data.followup?.date && (
                    <>
                      <Typography>
                        {`Follow up on ${
                          convertADtoBS(moment(data.followup.date).toDate()).formatted2
                        } (${moment(data.followup.date).format("YYYY-MM-DD")} AD) ${
                          data.followup.startDate
                            ? ` from ${moment(data.followup.startDate).format(
                                "h:mm A"
                              )} to ${moment(data.followup.endDate).format("h:mm A")}`
                            : ""
                        }`}
                      </Typography>

                      {data.followup.note && (
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                          {data.followup.note}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.ADVICE:
      return (
        <Box>
          {data.advice && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.advice}
                  </Box>
                </Typography>

                <Typography>
                  <Box component="span" whiteSpace="pre-line">
                    {data.advice}
                  </Box>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.OTHER_SERVICES:
      return (
        <Box>
          {data.otherServices?.length > 0 && (
            <>
              <Box pb={2}>
                <Typography pt={3} fontWeight={600} component="span">
                  {assessmentLabels.otherServices}
                </Typography>

                <ol style={{ paddingLeft: "1.5rem", margin: 0 }}>
                  {data.otherServices.map(({ name, id }) => (
                    <li key={id}>
                      <Typography>{name}</Typography>
                    </li>
                  ))}
                </ol>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.DENTAL_SERVICES:
      return (
        <Box>
          {data.dentalServices?.length > 0 && (
            <>
              <Box pb={2}>
                <Typography pt={3} fontWeight={600} component="span">
                  {assessmentLabels.dentalServices}
                </Typography>

                <ol style={{ paddingLeft: "1.5rem", margin: 0 }}>
                  {data.dentalServices.map(({ name, id, note, teeth = [] }) => (
                    <li key={id}>
                      <Typography>{name}</Typography>

                      {teeth?.length > 0 && <Typography>{`Teeth: ${teeth.join(", ")}`}</Typography>}

                      {note && <Typography>Note: {note}</Typography>}
                    </li>
                  ))}
                </ol>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.XRAY_SERVICES:
      return (
        <Box>
          {data.xrayServices?.length > 0 && (
            <>
              <Box pb={2}>
                <Typography pt={3} fontWeight={600} component="span">
                  {assessmentLabels.xrayServices}
                </Typography>

                <ol style={{ paddingLeft: "1.5rem", margin: 0 }}>
                  {data.xrayServices.map(({ name, id, note, teeth = [] }) => (
                    <li key={id}>
                      <Typography>{name}</Typography>

                      {teeth?.length > 0 && <Typography>{`Teeth: ${teeth.join(", ")}`}</Typography>}

                      {note && <Typography>Note: {note}</Typography>}
                    </li>
                  ))}
                </ol>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.TREATMENT_DONE:
      return (
        <Box>
          {!!data.treatmentDone?.some((i) => i.checked) && (
            <>
              <Box pb={2}>
                <Typography pt={3} fontWeight={600} component="span">
                  {assessmentLabels.treatmentDone}
                </Typography>

                <ol style={{ paddingLeft: "1.5rem", margin: 0 }}>
                  {data.treatmentDone
                    .filter((item) => item.checked)
                    .map(({ service }) => (
                      <li key={service.id}>
                        <Typography>{service.name}</Typography>
                        {Boolean(service.document?.length) && (
                          <Box component="ul" pl={2}>
                            {service.document
                              ?.filter((item) => item.checked)
                              .map((subItem) => (
                                <li key={subItem.service.id}>
                                  <Typography>{subItem.service.name}</Typography>
                                </li>
                              ))}
                          </Box>
                        )}
                      </li>
                    ))}
                </ol>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.OTHERS:
      return (
        <Box>
          {data?.others && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.others}
                  </Box>
                </Typography>

                <Typography>
                  <Box component="span">{data.others}</Box>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.DERMASCOPY:
      return (
        <Box>
          {data.dermascopy && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.dermascopy || "Dermascopy"}
                  </Box>
                </Typography>

                <Typography>
                  <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                    {data.dermascopy}
                  </Box>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.OTHER1:
      return (
        <Box>
          {data.other1 && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.other1 || "Other1"}
                  </Box>
                </Typography>

                <Typography>
                  <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                    {data.other1}
                  </Box>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.OTHER2:
      return (
        <Box>
          {data.other2 && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.other2 || "Other2"}
                  </Box>
                </Typography>

                <Typography>
                  <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                    {data.other2}
                  </Box>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.OTHER3:
      return (
        <Box>
          {data.other3 && (
            <>
              <Box pb={2}>
                <Typography>
                  <Box pt={3} fontWeight={600} component="span">
                    {assessmentLabels.other3 || "Other3"}
                  </Box>
                </Typography>

                <Typography>
                  <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                    {data.other3}
                  </Box>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      );

    case SORT_ORDER.SKIN_ANALYSIS:
      return data?.skinAnalysis?.length > 0 ? (
        <>
          <Typography fontWeight="bold" marginTop="10px">
            {assessmentLabels.skinAnalysis}:
          </Typography>
          <Box
            sx={{
              width: "100%"
            }}
          >
            {data.skinAnalysis.map((skinData) => (
              <Box key={skinData.dbKey} sx={{ display: "flex", alignItems: "center" }}>
                <Typography fontWeight="bold">{startCase(skinData.name)}</Typography>
                <Typography> : {skinData.reading}</Typography>
              </Box>
            ))}
          </Box>
        </>
      ) : null;

    default:
      return null;
  }
};
